import React from 'react';
import { Form, Row, Col, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const PersonalProfile = ({ profile, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...profile, [field]: value });
  };

  return (
      <Row>
        {/* Age */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="user" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>How old are you?</Form.Label>
              <InputGroup>
                <Form.Control
                    type="number"
                    value={profile.age ?? ''}
                    onChange={(e) => handleChange('age', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('age', 0); }}
                    min={18}
                    max={100}
                />
                <InputGroup.Text>years</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Your age affects investment time horizon and risk capacity
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Marital Status */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="heart" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>What is your marital/partnership status?</Form.Label>
              <Form.Select
                  value={profile.maritalStatus}
                  onChange={(e) => handleChange('maritalStatus', e.target.value)}
              >
                <option value="single">Single</option>
                <option value="married">Married / Civil Partnership</option>
                <option value="divorced">Divorced / Separated</option>
                <option value="widowed">Widowed</option>
                <option value="cohabiting">Cohabiting</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Col>

        {/* Dependents */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon success">
              <FontAwesomeIcon icon="users" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Do you have children or other financial dependants?</Form.Label>
              <Form.Control
                  type="number"
                  value={profile.dependents ?? ''}
                  onChange={(e) => handleChange('dependents', e.target.value === '' ? '' : parseInt(e.target.value))}
                  onBlur={(e) => { if (e.target.value === '') handleChange('dependents', 0); }}
                  min={0}
                  max={10}
              />
              <Form.Text className="text-muted">
                Number of people who rely on you financially
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Employment Status */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon warning">
              <FontAwesomeIcon icon="briefcase" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>What is your employment status?</Form.Label>
              <Form.Select
                  value={profile.employmentStatus}
                  onChange={(e) => handleChange('employmentStatus', e.target.value)}
              >
                <option value="employed">Employed (Full-time)</option>
                <option value="employed_part">Employed (Part-time)</option>
                <option value="self_employed">Self-employed</option>
                <option value="contractor">Contractor</option>
                <option value="retired">Retired</option>
                <option value="not_working">Not currently working</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Affects income stability assessment
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* UK Resident */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="globe" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Are you a UK resident and UK domiciled?</Form.Label>
              <Form.Select
                  value={profile.ukResident ? 'yes' : 'no'}
                  onChange={(e) => handleChange('ukResident', e.target.value === 'yes')}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Required for UK pension advice
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Expected Life Changes */}
        <Col md={12}>
          <div className="profile-card">
            <div className="card-icon warning">
              <FontAwesomeIcon icon="sync-alt" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Do you expect any major life changes in the next 5-10 years?</Form.Label>
              <Form.Text className="text-muted d-block mb-2">
                Select all that apply — these may affect your financial planning
              </Form.Text>
              <div className="d-flex flex-wrap gap-2">
                {[
                  { value: 'retirement', label: 'Retirement' },
                  { value: 'divorce', label: 'Divorce / Separation' },
                  { value: 'caring', label: 'Caring responsibilities' },
                  { value: 'career_change', label: 'Career change' },
                  { value: 'relocation', label: 'Relocation' },
                  { value: 'inheritance', label: 'Expected inheritance' },
                  { value: 'none', label: 'None expected' }
                ].map((option) => (
                    <Form.Check
                        key={option.value}
                        type="checkbox"
                        id={`life-change-${option.value}`}
                        label={option.label}
                        checked={(profile.expectedLifeChanges || []).includes(option.value)}
                        onChange={(e) => {
                          const current = profile.expectedLifeChanges || [];
                          if (e.target.checked) {
                            // If selecting 'none', clear others; if selecting others, remove 'none'
                            if (option.value === 'none') {
                              handleChange('expectedLifeChanges', ['none']);
                            } else {
                              handleChange('expectedLifeChanges', [...current.filter(v => v !== 'none'), option.value]);
                            }
                          } else {
                            handleChange('expectedLifeChanges', current.filter(v => v !== option.value));
                          }
                        }}
                        className="me-3"
                    />
                ))}
              </div>
            </Form.Group>
          </div>
        </Col>
      </Row>
  );
};

export default PersonalProfile;